import {
  createInitialState,
  transition,
  type MachineConfig,
  type MachineEffect,
  type MachineEvent,
  type MachineState,
} from '../domain/machine';

export type Dispatch = (event: MachineEvent) => void;
export type EffectRunner = (effect: MachineEffect, dispatch: Dispatch) => void;

/**
 * Holds the machine state outside React so it can be read with `useSyncExternalStore`, driven by
 * a ticker, and exercised directly in tests. Effects run after the state is committed, in order.
 */
export class MachineStore {
  #state: MachineState;
  readonly #listeners = new Set<() => void>();
  readonly #config: MachineConfig;
  readonly #runEffect: EffectRunner;

  constructor(config: MachineConfig, runEffect: EffectRunner, initial?: MachineState) {
    this.#config = config;
    this.#runEffect = runEffect;
    this.#state = initial ?? createInitialState(config);
  }

  readonly getState = (): MachineState => this.#state;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  };

  readonly dispatch: Dispatch = (event) => {
    const { state, effects } = transition(this.#state, event, this.#config);
    if (state !== this.#state) {
      this.#state = state;
      for (const listener of this.#listeners) listener();
    }
    for (const effect of effects) this.#runEffect(effect, this.dispatch);
  };
}

export interface TickerOptions {
  readonly intervalMs?: number;
  readonly monotonicNow?: () => number;
  readonly wallClockNow?: () => number;
}

/**
 * Sends TICK events carrying the real time elapsed since the last tick, so phase timers stay
 * accurate even when the browser throttles or delays the interval. Returns a stop function.
 */
export function startTicker(
  dispatch: Dispatch,
  {
    intervalMs = 100,
    monotonicNow = () => performance.now(),
    wallClockNow = () => Date.now(),
  }: TickerOptions = {},
): () => void {
  let last = monotonicNow();
  const id = setInterval(() => {
    const now = monotonicNow();
    const dt = (now - last) / 1000;
    last = now;
    dispatch({ type: 'TICK', dt, now: wallClockNow() });
  }, intervalMs);
  return () => clearInterval(id);
}
